import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  clampStepIndex,
  needsScrollForRoom,
  placeTooltip,
  sameBox,
  spotlightBox,
} from '../../utils/demoTour';
import { useTranslation } from '../../translations';

// Silnik przewodników: przechodzi po realnych ekranach, podświetla konkretny element
// interfejsu i stawia przy nim dymek z opisem. Sam nie decyduje, KIEDY się pokazać
// ani co znaczy „widziane" — to robią komponenty nad nim: DemoTour.js (spacer po
// aplikacji dla org demo) i PageTour.js (mini-przewodniki pojedynczych stron).
// Geometria (spotlightBox/placeTooltip) — utils/demoTour.js.
// Gdy elementu nie ma na ekranie (zwinięty sidebar na telefonie, pozycja tylko dla
// admina), krok degraduje się do karty na środku — przewodnik nigdy nie zawisa.

const TARGET_LOOKUP_TIMEOUT_MS = 2500;
const TARGET_LOOKUP_INTERVAL_MS = 100;
// Tyle najdłużej karta czeka niewidoczna na znalezienie elementu, zanim pokaże się na środku.
const REVEAL_FALLBACK_MS = 400;
// Odstęp od górnej krawędzi po przewinięciu: miejsce na przyklejone paski (pasek akcji
// konstruktorów, górny pasek na telefonie), żeby nie zasłoniły podświetlonego elementu.
const SCROLL_TOP_OFFSET_PX = 96;

// Przewija tak, żeby element stał przy górnej krawędzi: pod nim zostaje miejsce na kartę.
// `scroll-margin-top` działa z `scrollIntoView` także w zagnieżdżonych kontenerach
// przewijania (tu przewija się `#root`, nie okno).
function scrollTargetIntoView(el) {
  const poprzedni = el.style.scrollMarginTop;
  el.style.scrollMarginTop = `${SCROLL_TOP_OFFSET_PX}px`;
  el.scrollIntoView({ block: 'start', inline: 'nearest' });
  el.style.scrollMarginTop = poprzedni;
}

// Blokuje klikanie w aplikację pod spodem. Przy podświetleniu jest przezroczysty
// (przyciemnienie robi cień spotlightu), bez podświetlenia sam przyciemnia ekran.
// z-index ponad wszystkim w aplikacji (sidebar 20001/20002, modale kalendarza 25000) —
// niżej podświetlenie menu schowałoby się pod samym menu.
const Blocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-tour);
  background: ${props => (props.$dim ? 'rgba(15, 27, 45, 0.55)' : 'transparent')};
  /* Bez globalnego przejścia koloru tła (index.css): pomiar celu wymusza przeliczenie
     stylów jeszcze w stanie „bez celu”, więc każde otwarcie z podświetleniem zaczynało
     się od ~160 ms gasnącego przyciemnienia. */
  transition: none;
`;

const Spotlight = styled.div`
  position: fixed;
  z-index: calc(var(--z-tour) + 1);
  border-radius: var(--r-md);
  pointer-events: none;
  border: 2px solid var(--primary);
  box-shadow: 0 0 0 9999px rgba(15, 27, 45, 0.6);
  transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease;
`;

const Card = styled.div`
  position: fixed;
  z-index: calc(var(--z-tour) + 2);
  width: min(420px, calc(100vw - 24px));
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
  border-radius: var(--r-lg);
  background: var(--card-bg);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  color: var(--text);

  ${props => props.$centered && `
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}

  /* Szeroka karta TYLKO bez podświetlenia. Gdy jest cel, karta ma tę samą szerokość także
     wtedy, gdy obok celu się nie mieści i ląduje na środku: pozycję liczymy z wymiarów
     karty, więc szerokość zależna od wyniku tego liczenia przełączała go w kółko
     (środek, obok celu, środek…; zmierzone na /price-rules). */
  ${props => props.$wide && `
    width: min(560px, calc(100vw - 32px));
  `}
`;

const Counter = styled.div`
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--primary);
`;

const Title = styled.h2`
  margin: 0;
  font-size: var(--fs-lg);
  line-height: 1.3;
`;

const Lead = styled.p`
  margin: 0;
  color: var(--text-muted);
  line-height: 1.5;
`;

const Points = styled.ul`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  line-height: 1.5;
  font-size: var(--fs-base);
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => (props.$active ? 'var(--primary)' : 'var(--border)')};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid var(--border);
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const BaseButton = styled.button`
  padding: 8px 15px;
  border-radius: var(--r-sm);
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
`;

const SecondaryButton = styled(BaseButton)`
  background: var(--button-secondary-bg);
  color: var(--button-secondary-text);
  border: 1px solid var(--button-secondary-border);
`;

const PrimaryButton = styled(BaseButton)`
  background: var(--primary);
  color: var(--primary-contrast);
  border: 1px solid var(--primary);

  &:hover { background: var(--primary-hover); }
`;

const SkipButton = styled.button`
  padding: 8px 4px;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: var(--fs-sm);
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;

const viewportSize = () => ({ width: window.innerWidth, height: window.innerHeight });

export default function TourOverlay({ open, steps, onClose, testId = 'demo-tour', finishLabel = 'Zaczynam' }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [box, setBox] = useState(null);            // ramka podświetlenia (null = karta na środku)
  const [pos, setPos] = useState({ placement: 'center' });
  const [revealed, setRevealed] = useState(true);  // false = szukamy elementu, karta niewidoczna
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef(null);

  const total = Array.isArray(steps) ? steps.length : 0;
  const index = clampStepIndex(stepIndex, total);
  const step = total ? steps[index] : null;
  const isLast = index === total - 1;

  const close = onClose;

  // Efekty „layout”, nie zwykłe: muszą zdążyć PRZED narysowaniem klatki. Inaczej pierwsza
  // klatka nowego kroku albo przewodnika innej strony pokazywała numer kroku i ramkę
  // z poprzedniego (mignięcie podświetlenia w złym miejscu).
  useLayoutEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Krok może chcieć innego ekranu — przechodzimy tam, zanim zaczniemy szukać elementu.
  // Też przed narysowaniem klatki: inaczej pierwsza klatka kroku pokazywała jego tekst
  // przy celu z kroku poprzedniego, a karta zaraz potem przeskakiwała na drugi koniec ekranu.
  useLayoutEffect(() => {
    if (!open) return;
    if (step?.route && location.pathname !== step.route) navigate(step.route);
  }, [open, step, location.pathname, navigate]);

  // Szukanie elementu do podświetlenia. Po zmianie trasy strona montuje się z opóźnieniem,
  // więc odpytujemy cyklicznie aż do timeoutu; brak elementu = karta na środku.
  //
  // Dwie rzeczy, które sprawiają, że przewodnik wygląda tak samo na każdej stronie i ekranie:
  // - element poza ekranem albo bez miejsca na kartę obok jest najpierw PRZEWIJANY (raz na
  //   krok). Bez tego ten sam krok na laptopie spadał na kartę na środku, a na dużym monitorze
  //   miał podświetlenie;
  // - karta jest niewidoczna, dopóki elementu szukamy (najwyżej REVEAL_FALLBACK_MS), bo
  //   inaczej najpierw mignęła na środku ekranu, a chwilę później skakała obok elementu.
  useLayoutEffect(() => {
    if (!open) return undefined;
    if (!step?.target) { setBox(null); setRevealed(true); return undefined; }
    if (step.route && location.pathname !== step.route) return undefined;

    let cancelled = false;
    let timer = null;
    let przewiniety = false;
    const deadline = Date.now() + TARGET_LOOKUP_TIMEOUT_MS;
    const revealTimer = setTimeout(() => { if (!cancelled) setRevealed(true); }, REVEAL_FALLBACK_MS);

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      let rect = el ? el.getBoundingClientRect() : null;
      if (el && !przewiniety && rect.width > 0 && rect.height > 0) {
        przewiniety = true;
        if (needsScrollForRoom(rect, viewportSize())) {
          scrollTargetIntoView(el);
          rect = el.getBoundingClientRect();
        }
      }
      const next = rect ? spotlightBox(rect, viewportSize()) : null;
      if (next) {
        clearTimeout(revealTimer);
        setBox(next);
        setRevealed(true);
        return;
      }
      if (Date.now() >= deadline) { setBox(null); setRevealed(true); return; }
      timer = setTimeout(measure, TARGET_LOOKUP_INTERVAL_MS);
    };

    setBox(null);
    setRevealed(false);
    measure();
    return () => {
      cancelled = true;
      clearTimeout(revealTimer);
      if (timer) clearTimeout(timer);
    };
  }, [open, step, location.pathname]);

  // Ramka musi nadążać za elementem nie tylko przy przewijaniu i zmianie rozmiaru okna, ale też
  // gdy układ przesunie się PO jego znalezieniu: doczytane dane nad nim, zwinięte menu, koniec
  // animacji. Bez tego ramka zostawała kilka pikseli obok (page_tours.cy.js padał losowo).
  // Pomiar najwyżej raz na klatkę, a stan zmienia się tylko przy realnej zmianie ramki: własne
  // przerysowanie przewodnika też jest zmianą DOM i inaczej zapętliłoby obserwatora.
  useEffect(() => {
    if (!open || !step?.target) return undefined;
    let klatka = null;
    const recompute = () => {
      klatka = null;
      const el = document.querySelector(step.target);
      const next = el ? spotlightBox(el.getBoundingClientRect(), viewportSize()) : null;
      setBox((prev) => (sameBox(prev, next) ? prev : next));
    };
    const zaplanuj = () => { if (klatka === null) klatka = requestAnimationFrame(recompute); };
    const obserwator = new MutationObserver(zaplanuj);
    obserwator.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
    window.addEventListener('resize', zaplanuj);
    window.addEventListener('scroll', zaplanuj, true);
    document.addEventListener('transitionend', zaplanuj, true);
    return () => {
      obserwator.disconnect();
      if (klatka !== null) cancelAnimationFrame(klatka);
      window.removeEventListener('resize', zaplanuj);
      window.removeEventListener('scroll', zaplanuj, true);
      document.removeEventListener('transitionend', zaplanuj, true);
    };
  }, [open, step]);

  // Pozycja dymka liczona PO wyrenderowaniu karty — potrzebujemy jej realnych wymiarów.
  useLayoutEffect(() => {
    if (!open) return;
    if (!box) { setPos({ placement: 'center' }); return; }
    const card = cardRef.current?.getBoundingClientRect();
    if (!card) return;
    setPos(placeTooltip({
      target: box,
      tooltip: { width: card.width, height: card.height },
      viewport: viewportSize(),
    }));
  }, [open, box, index]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight' && !isLast) setStepIndex(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) setStepIndex(index - 1);
    };
    document.addEventListener('keydown', onKeyDown);
    // Niewidocznej karty nie da się zogniskować, więc ponawiamy, gdy się pokaże.
    if (revealed) cardRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close, index, isLast, revealed]);

  if (!open || !step) return null;

  const centered = !box || pos.placement === 'center';
  const cardStyle = centered ? undefined : { top: `${pos.top}px`, left: `${pos.left}px` };

  // Przyciemnia albo warstwa, albo cień podświetlenia, nigdy obie naraz. Dawniej karta na
  // środku przy podświetleniu przyciemniała ekran podwójnie, a globalne przejście koloru
  // tła (index.css) zamieniało każde takie przełączenie w pulsowanie.
  return (
    <>
      <Blocker data-cy={testId} $dim={!box && revealed} onMouseDown={close} />
      {box && (
        <Spotlight
          data-cy={`${testId}-spotlight`}
          style={{ top: `${box.top}px`, left: `${box.left}px`, width: `${box.width}px`, height: `${box.height}px` }}
        />
      )}
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        tabIndex={-1}
        ref={cardRef}
        $centered={centered}
        $wide={!box}
        style={revealed ? cardStyle : { ...cardStyle, visibility: 'hidden' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Counter data-cy={`${testId}-counter`}>{t('pageTours.counter', { current: index + 1, total })}</Counter>
        <Title id={`${testId}-title`}>{step.title}</Title>
        <Lead>{step.lead}</Lead>
        <Points>
          {step.points.map((point) => <li key={point}>{point}</li>)}
        </Points>
        <Footer>
          <Dots aria-hidden="true">
            {steps.map((s, i) => <Dot key={s.id} $active={i === index} />)}
          </Dots>
          {!isLast && (
            <SkipButton type="button" onClick={close} data-cy={`${testId}-skip`}>
              {t('pageTours.skip')}
            </SkipButton>
          )}
          <Buttons>
            {index > 0 && (
              <SecondaryButton type="button" onClick={() => setStepIndex(index - 1)} data-cy={`${testId}-prev`}>
                {t('pageTours.prev')}
              </SecondaryButton>
            )}
            <PrimaryButton
              type="button"
              onClick={() => (isLast ? close() : setStepIndex(index + 1))}
              data-cy={`${testId}-next`}
            >
              {isLast ? finishLabel : t('pageTours.next')}
            </PrimaryButton>
          </Buttons>
        </Footer>
      </Card>
    </>
  );
}
